// Límites de tipo de paquete por carrier. El frontend los consulta al endpoint
// getPackageLimits (API de límites) vía loadPackageLimits(); la sugerencia de
// Tarima y el aviso (con las medidas de límite) se basan en esa consulta.
// BUILTIN_LIMITS es solo un fallback mientras la API responde.

import { base44 } from "@/api/base44Client";

const BUILTIN_LIMITS = [
  { carrier: "Paquetexpress", maxDim: 80, maxWidth: 60, maxWeight: 65, verified: true },
  { carrier: "Estafeta", maxDim: 100, maxWidth: 100, maxWeight: 70, verified: false },
  { carrier: "Redpack", maxDim: 100, maxWidth: 100, maxWeight: 70, verified: false },
  { carrier: "J&T", maxDim: 100, maxWidth: 100, maxWeight: 70, verified: false },
  { carrier: "Nuvee", maxDim: 100, maxWidth: 100, maxWeight: 70, verified: false },
  { carrier: "Carssa", maxDim: 100, maxWidth: 100, maxWeight: 70, verified: false },
  { carrier: "T1negocios", maxDim: 100, maxWidth: 100, maxWeight: 70, verified: false },
  { carrier: "Guatex", maxDim: 100, maxWidth: 100, maxWeight: 70, verified: false },
  { carrier: "Drive", maxDim: 100, maxWidth: 100, maxWeight: 70, verified: false },
  { carrier: "Quetzal", maxDim: 100, maxWidth: 100, maxWeight: 70, verified: false },
  { carrier: "99Minutos", maxDim: 100, maxWidth: 100, maxWeight: 25, verified: false },
  { carrier: "DHL", maxDim: 120, maxWidth: 120, maxWeight: 70, verified: false },
  { carrier: "FedEx", maxDim: 150, maxWidth: 120, maxWeight: 68, verified: false },
  { carrier: "UPS", maxDim: 165, maxWidth: 120, maxWeight: 70, verified: false }
];

export const CARRIER_BOX_LIMITS = BUILTIN_LIMITS;
const BUILTIN_DEFAULT = { maxDim: 100, maxWidth: 100, maxWeight: 70, verified: false };
// Límite general de Sobre. Los sobres de las paqueterías mexicanas (Epak/Envia)
// aceptan máx. 35cm × 24cm × 1.5cm y 0.5 kg; si se excede, se sugiere Paquete.
// Antes se usaba 2 kg (estimado) y la advertencia no disparaba con 1 kg, lo que
// dejaba cotizar y generar como Sobre y fallaba en la API con un error confuso.
const BUILTIN_SOBRE = { maxDim: 35, maxWidth: 24, maxWeight: 0.5, verified: true };

let _cache = null;
let _loadPromise = null;

// Consulta la API de límites (getPackageLimits) y cachea el resultado en el
// módulo. Devuelve los límites (o null si falla). Es idempotente: llamadas
// repetidas reutilizan la misma promesa.
export async function loadPackageLimits() {
  if (_cache) return _cache;
  if (!_loadPromise) {
    _loadPromise = base44.functions.invoke("getPackageLimits")
      .then(res => { _cache = res.data || null; return _cache; })
      .catch(() => { _cache = null; return null; });
  }
  return _loadPromise;
}

const exceedsPaquete = (c, L, W, H, kg) =>
  H > c.maxDim || L > c.maxDim || W > c.maxWidth || kg > c.maxWeight;
const exceedsSobre = (s, L, W, H, kg) =>
  H > s.maxDim || L > s.maxDim || W > s.maxWidth || kg > s.maxWeight;

// Orden de los tipos de envío (de menor a mayor). La sugerencia solo escala
// (Sobre → Paquete → Tarima); nunca degrada.
const RANK = { sobre: 1, paquete: 2, tarima: 3, camion: 4 };

// Devuelve { suggested, warning } según dimensiones (cm) y peso (kg).
// Escala el tipo de envío cuando las dimensiones exceden el límite del carrier:
// Sobre → Paquete → Tarima. Nunca degrada (no baja de Tarima a Paquete, etc.).
// `carrier` (opcional) = carrier elegido en el borrador o tarifa seleccionada.
// Cuando se conoce, el aviso y la sugerencia son SOLO de ese carrier (con sus
// medidas de límite tomadas de la API). Sin carrier, aviso general conciso.
export function recommendPackageType(currentType, weightKg, dimsCm, carrier) {
  const L = Number(dimsCm?.length) || 0;
  const W = Number(dimsCm?.width) || 0;
  const H = Number(dimsCm?.height) || 0;
  const kg = Number(weightKg) || 0;
  // Sin dimensiones (Largo/Ancho/Alto) no hay aviso ni escalado: el tipo se
  // cotiza solo por peso. Solo al ingresar dimensiones se sugiere/ajusta el
  // tipo correspondiente según los límites del carrier.
  if (!(L > 0 || W > 0 || H > 0)) {
    return { suggested: currentType || "paquete", warning: "" };
  }

  const limits = (_cache && _cache.limits) || BUILTIN_LIMITS;
  const defaultLimit = (_cache && _cache.default) || BUILTIN_DEFAULT;
  const sobreLimit = (_cache && _cache.sobre) || BUILTIN_SOBRE;

  const warnPaquete = (c) =>
    `Las dimensiones exceden el límite de Paquete de ${c.carrier || "el carrier"} (máx. ${c.maxDim}cm${c.maxWidth !== c.maxDim ? ` / ancho ${c.maxWidth}cm` : ""} / peso ${c.maxWeight}kg${c.verified ? " (vía API)" : ""}). Se sugiere Tarima; si generas como Paquete, la guía puede no emitirse.`;
  const warnSobre = () =>
    `Las dimensiones exceden el límite de Sobre (máx. ${sobreLimit.maxDim}cm${sobreLimit.maxWidth !== sobreLimit.maxDim ? ` / ancho ${sobreLimit.maxWidth}cm` : ""} / peso ${sobreLimit.maxWeight}kg${sobreLimit.verified ? " (vía API)" : ""}). Se sugiere Paquete; si generas como Sobre, la guía puede no emitirse.`;

  // Carrier conocido: aviso y sugerencia SOLO de ese carrier, con sus medidas
  // de límite (desde la API de límites). Si el carrier no está listado, se usa
  // el límite por defecto (estimado) para que la sugerencia funcione igual.
  if (carrier) {
    const c = { carrier, ...(limits.find(x => x.carrier === carrier) || defaultLimit) };
    // Si ya es Tarima/Camión, no escalamos (no hay límite superior conocido).
    if (RANK[currentType] >= RANK.tarima) return { suggested: currentType, warning: "" };
    if (exceedsPaquete(c, L, W, H, kg)) return { suggested: "tarima", warning: warnPaquete(c) };
    if (currentType === "sobre" && exceedsSobre(sobreLimit, L, W, H, kg)) {
      return { suggested: "paquete", warning: warnSobre() };
    }
    return { suggested: currentType || "paquete", warning: "" };
  }

  // Sin carrier elegido: usa el límite por defecto (general de Paquete) como
  // umbral de escalado a Tarima, así la sugerencia Sobre→Paquete→Tarima
  // funciona aunque no se haya elegido paquetería todavía (antes exigía exceder
  // TODOS los carriers, incluyendo DHL/FedEx/UPS que aceptan paquetes grandes,
  // por lo que Paquete→Tarima nunca disparaba).
  if (RANK[currentType] >= RANK.tarima) return { suggested: currentType, warning: "" };
  if (exceedsPaquete(defaultLimit, L, W, H, kg)) {
    return { suggested: "tarima", warning: "Las dimensiones exceden el límite de Paquete. Se sugiere Tarima (pallet); al elegir tu paquetería se confirmará si conviene Tarima." };
  }
  if (currentType === "sobre" && exceedsSobre(sobreLimit, L, W, H, kg)) {
    return { suggested: "paquete", warning: "Las dimensiones exceden el límite de Sobre. Se sugiere Paquete." };
  }
  return { suggested: currentType || "paquete", warning: "" };
}